import React from "react";

function renderInline(text, keyPrefix = "") {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p))
      return <strong key={`${keyPrefix}-${i}`} className="font-semibold text-gray-900">{p.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(p))
      return <em key={`${keyPrefix}-${i}`}>{p.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(p))
      return (
        <code key={`${keyPrefix}-${i}`}
          className="font-mono text-[0.85em] px-1.5 py-0.5 rounded bg-gray-100 text-pink-600 border border-gray-200">
          {p.slice(1, -1)}
        </code>
      );
    return p;
  });
}

function parseBlocks(content) {
  const lines = content.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim() || "code";
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      blocks.push({ type: "code", lang, content: codeLines.join("\n") });
      continue;
    }

    // H1
    if (/^# /.test(line)) {
      blocks.push({ type: "h1", content: line.slice(2) });
      i++;
      continue;
    }

    // H2
    if (/^## /.test(line)) {
      blocks.push({ type: "h2", content: line.slice(3) });
      i++;
      continue;
    }

    // H3
    if (/^### /.test(line)) {
      blocks.push({ type: "h3", content: line.slice(4) });
      i++;
      continue;
    }

    // HR
    if (line.trim() === "---") {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    // Table (simple — starts and ends with |)
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        if (!/^\|[-| :]+\|$/.test(lines[i].trim())) {
          rows.push(lines[i].trim().split("|").filter((c) => c !== "").map((c) => c.trim()));
        }
        i++;
      }
      blocks.push({ type: "table", rows });
      continue;
    }

    // Bullet list
    if (/^- /.test(line)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Numbered list
    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph (collect consecutive text lines)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^#{1,3} /.test(lines[i]) &&
      !/^- /.test(lines[i]) &&
      !/^\d+\. /.test(lines[i]) &&
      !lines[i].startsWith("```") &&
      !/^\|/.test(lines[i]) &&
      lines[i].trim() !== "---"
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: "p", content: paraLines.join("\n") });
    }
  }

  return blocks;
}

export default function MarkdownRenderer({ content, className = "" }) {
  if (!content) return <p className="text-gray-400 italic">No content.</p>;

  const blocks = parseBlocks(content);

  return (
    <div className={`markdown-body ${className}`}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "h1":
            return (
              <h1 key={idx} className="text-2xl font-extrabold text-gray-900 mt-10 mb-4 leading-tight">
                {renderInline(block.content, `h1-${idx}`)}
              </h1>
            );
          case "h2":
            return (
              <h2 key={idx} className="text-lg font-bold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-100">
                {renderInline(block.content, `h2-${idx}`)}
              </h2>
            );
          case "h3":
            return (
              <h3 key={idx} className="text-base font-bold text-gray-800 mt-6 mb-2">
                {renderInline(block.content, `h3-${idx}`)}
              </h3>
            );
          case "p": {
            const text = block.content;
            // Detect all-bold line (acts as a sub-heading)
            if (/^\*\*[^*]+\*\*$/.test(text.trim())) {
              return (
                <p key={idx} className="text-base font-semibold text-gray-900 mt-6 mb-2">
                  {renderInline(text, `p-${idx}`)}
                </p>
              );
            }
            return (
              <p key={idx} className="text-gray-700 leading-relaxed mb-4 text-[15px]">
                {renderInline(text, `p-${idx}`)}
              </p>
            );
          }
          case "ul":
            return (
              <ul key={idx} className="mb-4 space-y-1.5 pl-1">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-2.5 text-gray-700 text-[15px] leading-relaxed">
                    <span className="flex-shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full bg-brandPrimary" style={{ background: "#ea3d95" }} />
                    <span>{renderInline(item, `ul-${idx}-${j}`)}</span>
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={idx} className="mb-4 space-y-1.5 pl-1 counter-reset-list">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-3 text-gray-700 text-[15px] leading-relaxed">
                    <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full text-xs font-bold flex items-center justify-center text-white"
                      style={{ background: "#ea3d95", minWidth: "20px" }}>
                      {j + 1}
                    </span>
                    <span>{renderInline(item, `ol-${idx}-${j}`)}</span>
                  </li>
                ))}
              </ol>
            );
          case "hr":
            return <hr key={idx} className="my-8 border-gray-100" />;
          case "table":
            return (
              <div key={idx} className="mb-6 overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  {block.rows.length > 0 && (
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {block.rows[0].map((cell, j) => (
                          <th key={j} className="px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase tracking-wider">
                            {renderInline(cell, `th-${idx}-${j}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {block.rows.slice(1).map((row, j) => (
                      <tr key={j} className={`border-b border-gray-100 ${j % 2 === 1 ? "bg-gray-50/50" : "bg-white"}`}>
                        {row.map((cell, k) => (
                          <td key={k} className="px-4 py-3 text-gray-700">
                            {renderInline(cell, `td-${idx}-${j}-${k}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "code":
            return (
              <div key={idx} className="mb-6 rounded-xl overflow-hidden border border-gray-800 shadow-lg">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-semibold text-gray-500 uppercase tracking-widest">
                      {block.lang}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 opacity-70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 opacity-70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 opacity-70" />
                  </div>
                </div>
                <pre className="overflow-x-auto bg-[#0d1117] p-5">
                  <code className="text-[13px] font-mono text-gray-300 leading-6 whitespace-pre">
                    {block.content}
                  </code>
                </pre>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
